import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Shield,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Eye,
  Edit3,
  Target,
  Banknote,
  Zap,
  Car,
  Bus,
  GraduationCap,
  Info,
  XCircle,
  RefreshCw,
  Users,
  Heart,
  Home,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import policyService from "../../api/PolicyService";
import Loader from "../../components/Loader";
import { toast } from "react-toastify";
import { formatFixedDurationLabel } from "../../components/PolicyFormComponents";
import { formatMoney } from "../../utils/formatMoney";

export default function PolicyDetails() {
  const navigate = useNavigate();
  const { policy_slug } = useParams();

  const [policy, setPolicy] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedPackages, setExpandedPackages] = useState({});

  useEffect(() => {
    const fetchPolicyDetails = async () => {
      if (!policy_slug) {
        setError("No policy identifier provided");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await policyService.getPolicy(policy_slug);
        
        // Handle different response structures
        let policyData = null;
        
        if (response?.status && response?.data) {
          // Response from handleApiSuccess
          const data = response.data?.data?.data || response.data?.data;
          
          if (data?.status_code === 200 && data?.data) {
            policyData = data.data;
          } else if (data && !data.status_code) {
            // Direct data object
            policyData = data;
          } else if (response.data?.data) {
            // Nested data structure
            policyData = response.data.data;
          }
        } else if (response?.data?.data?.data) {
          // Direct nested structure
          const data = response.data.data.data;
          if (data.status_code === 200 && data.data) {
            policyData = data.data;
          }
        } else if (response?.data?.data) {
          policyData = response.data.data;
        }

        if (policyData) {
          const formattedPolicy = {
            ...policyData,
            created_at: policyData.created_at || new Date().toISOString(),
            updated_at: policyData.updated_at || new Date().toISOString(),
            status: policyData.status || "pending",
          };

          setPolicy(formattedPolicy);
        } else {
          const errorMessage = response?.message || response?.data?.data?.reason || "Failed to fetch policy details";
          toast.error(errorMessage);
          setError(errorMessage);
          setPolicy(null);
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.data?.reason || error?.message || "Failed to fetch policy details";
        toast.error(errorMessage);
        setError(errorMessage);
        setPolicy(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPolicyDetails();
  }, [policy_slug]);

  const UNIFIED_CARD_COLORS = {
    bgGradient: "from-emerald-500 to-green-600",
  };

  const getPolicyTypeInfo = (policyType, shortDescription, insuranceType) => {
    // Determine insurance type from short_description or insurance_type
    const insuranceTypeValue = shortDescription || insuranceType || "";
    
    // Life Insurance
    if (insuranceTypeValue === "life_insurance" || policyType === "life_insurance") {
      return {
        icon: Shield,
        label: "Life Insurance",
        ...UNIFIED_CARD_COLORS,
      };
    }
    
    // General Insurance - check subcategory
    if (insuranceTypeValue === "general_insurance") {
      const subcategoryMap = {
        health_insurance: {
          icon: Users,
          label: "Health Insurance",
          ...UNIFIED_CARD_COLORS,
        },
        home_insurance: {
          icon: FileText,
          label: "Home Insurance",
          ...UNIFIED_CARD_COLORS,
        },
        accident_insurance: {
          icon: AlertTriangle,
          label: "Accident Insurance",
          ...UNIFIED_CARD_COLORS,
        },
        fire_insurance: {
          icon: Zap,
          label: "Fire Insurance",
          ...UNIFIED_CARD_COLORS,
        },
        marine_insurance: {
          icon: FileText,
          label: "Marine Insurance",
          ...UNIFIED_CARD_COLORS,
        },
        liability_insurance: {
          icon: Shield,
          label: "Liability Insurance",
          ...UNIFIED_CARD_COLORS,
        },
      };
      
      if (subcategoryMap[policyType]) {
        return subcategoryMap[policyType];
      }
      
      // Default for general insurance
      return {
        icon: FileText,
        label: "General Insurance",
        ...UNIFIED_CARD_COLORS,
      };
    }
    
    // Travel Insurance types
    const travelTypeMap = {
      daily_commuter: {
        icon: Bus,
        label: "Daily Commuter",
        ...UNIFIED_CARD_COLORS,
      },
      intercity_travel: {
        icon: Car,
        label: "Intercity Travel",
        ...UNIFIED_CARD_COLORS,
      },
      okada_rider: {
        icon: Zap,
        label: "Motor Rider",
        ...UNIFIED_CARD_COLORS,
      },
      // Legacy types for backward compatibility
      students: {
        icon: GraduationCap,
        label: "Students",
        ...UNIFIED_CARD_COLORS,
      },
      okada: {
        icon: Zap,
        label: "Motor",
        ...UNIFIED_CARD_COLORS,
      },
      taxi: {
        icon: Car,
        label: "Taxi",
        ...UNIFIED_CARD_COLORS,
      },
    };

    return (
      travelTypeMap[policyType] || {
        icon: Shield,
        label: "General Policy",
        ...UNIFIED_CARD_COLORS,
      }
    );
  };


  const getStatusInfo = (status) => {
    const statusMap = {
      approved: { color: "green", label: "Approved", icon: CheckCircle },
      pending: { color: "yellow", label: "Pending Review", icon: Clock },
      rejected: { color: "red", label: "Rejected", icon: XCircle },
    };

    return statusMap[status] || statusMap.pending;
  };

  const formatCurrency = (amount) => formatMoney(amount);


  const togglePackage = (index) => {
    setExpandedPackages((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: Eye },
    { id: "coverage", label: "Coverage", icon: Shield },
    { id: "claims", label: "Claims Process", icon: FileText },
  ];

  if (isLoading) {
    return (
      <Loader
        fullScreen={true}
        text="Loading policy details..."
        subText="Please wait while we fetch the policy information"
      />
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-8 mx-auto max-w-md text-center bg-white rounded-2xl shadow-xl"
        >
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-red-100 rounded-full">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <h2 className="mb-2 text-2xl font-bold text-gray-900">
            Failed to Load Policy
          </h2>
          <p className="mb-6 text-gray-600">{error}</p>
          <div className="flex justify-center space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.reload()}
              className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
            >
              <RefreshCw className="mr-2 w-4 h-4" />
              Retry
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/policies")}
              className="flex items-center px-4 py-2 text-white bg-gray-600 rounded-lg transition-colors hover:bg-gray-700"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Policies
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!policy) return null;

  // Determine insurance type
  const insuranceType = policy.short_description || policy.insurance_type || "";
  const isTravelInsurance = insuranceType === "travel_insurance";
  const isLifeInsurance = insuranceType === "life_insurance";
  const isGeneralInsurance = insuranceType === "general_insurance";

  // Parse coverage durations
  const coverageDurations = Array.isArray(policy.coverage_durations) 
    ? policy.coverage_durations 
    : (typeof policy.coverage_durations === "string" 
        ? JSON.parse(policy.coverage_durations || "[]") 
        : []);

  const hasPackageBasedMaxClaims = isLifeInsurance || isGeneralInsurance || (isTravelInsurance && policy.policy_type === "travel_insurance");

  // Separate packages for Life and General Insurance
  const packages = (isLifeInsurance || isGeneralInsurance) 
    ? coverageDurations.map((pkg, idx) => ({
        ...pkg,
        id: idx,
        durationValue: pkg.duration ? pkg.duration.match(/^(\d+)/)?.[1] || "" : "",
        durationUnit: pkg.duration ? (pkg.duration.includes("year") ? "years" : "months") : "years",
        max_claims: pkg.max_claims ?? policy.max_claims ?? null,
        // Ensure per-package policy fields exist for rendering
        insurance_coverage_list: Array.isArray(pkg.insurance_coverage_list) ? pkg.insurance_coverage_list : [],
        exclusions: Array.isArray(pkg.exclusions) ? pkg.exclusions : [],
        eligibility_criteria: Array.isArray(pkg.eligibility_criteria) ? pkg.eligibility_criteria : [],
        claim_process_steps: Array.isArray(pkg.claim_process_steps) ? pkg.claim_process_steps : [],
        terms: pkg.terms || "",
      }))
    : [];

  // Packages for Travel Insurance (non-intercity, non-okada; i.e., travel_insurance)
  const travelPackages = (isTravelInsurance && policy.policy_type === "travel_insurance")
    ? coverageDurations.map((pkg, idx) => {
        // Parse duration - keep as-is for travel insurance
        return {
          ...pkg,
          id: idx,
          max_claims: pkg.max_claims ?? policy.max_claims ?? null,
          insurance_coverage_list: Array.isArray(pkg.insurance_coverage_list) ? pkg.insurance_coverage_list : [],
          exclusions: Array.isArray(pkg.exclusions) ? pkg.exclusions : [],
          eligibility_criteria: Array.isArray(pkg.eligibility_criteria) ? pkg.eligibility_criteria : [],
          claim_process_steps: Array.isArray(pkg.claim_process_steps) ? pkg.claim_process_steps : [],
          terms: pkg.terms || "",
        };
      })
    : [];

  // Helper function to get price/premium
  const getPrice = (item) => {
    if (item && typeof item === "object") {
      if (item.price !== undefined) return item.price;
      if (item.premium !== undefined) return item.premium;
    }
    return 0;
  };

  // Calculate price range
  const getPriceRange = () => {
    if (coverageDurations.length === 0) return { min: 0, max: 0 };
    const prices = coverageDurations.map(d => getPrice(d)).filter(p => p > 0);
    if (prices.length === 0) return { min: 0, max: 0 };
    return { min: Math.min(...prices), max: Math.max(...prices) };
  };

  const priceRange = getPriceRange();

  const typeInfo = getPolicyTypeInfo(policy.policy_type, policy.short_description, policy.insurance_type);
  const statusInfo = getStatusInfo(policy.status);
  const TypeIcon = typeInfo.icon;
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/50">
      <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-8"
        >
          <div className="flex flex-col space-y-4 lg:flex-row lg:items-start lg:justify-between lg:space-y-0">
            {/* Left section */}
            <div className="flex items-start space-x-3 sm:space-x-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => navigate("/policies")}
                className="flex-shrink-0 p-2 bg-white rounded-xl shadow-sm transition-all duration-200 hover:shadow-md"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </motion.button>

              <div className="flex-1 min-w-0">
                <div className="flex flex-col mb-2 space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
                  <div
                    className={`p-2 max-w-[50px] flex items-center justify-center  bg-gradient-to-r ${typeInfo.bgGradient} rounded-lg flex-shrink-0`}
                  >
                    <TypeIcon className="w-5 h-5 text-white sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-xl font-bold text-gray-900 break-words sm:text-2xl lg:text-3xl">
                      {policy.policy_name}
                    </h1>
                    <div className="flex flex-wrap gap-2 items-center mt-1">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        {typeInfo.label}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-700`}
                      >
                        <StatusIcon className="mr-1 w-3 h-3" />
                        {statusInfo.label}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-sm leading-relaxed text-gray-600 sm:text-base">
                  {policy.short_description}
                </p>
              </div>
            </div>

            {/* Right section - Actions */}
            <div className="flex justify-end items-center mt-4 space-x-2 lg:justify-start lg:mt-0">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(`/policies/edit/${policy.slug}`)}
                className="flex items-center px-3 py-2 text-sm text-orange-600 bg-orange-50 rounded-xl transition-colors sm:px-4 hover:bg-orange-100 sm:text-base"
              >
                <Edit3 className="mr-1 w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Edit Policy</span>
                <span className="sm:hidden">Edit</span>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6 sm:mb-8"
        >
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
            <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm transition-shadow sm:p-4 hover:shadow-md">
              <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
                <div className="flex-shrink-0 self-start p-2 bg-emerald-100 rounded-lg">
                  <Banknote className="w-4 h-4 text-emerald-600 sm:w-5 sm:h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="mb-1 text-xs text-gray-500">Price Range</p>
                  <p className="text-xs font-bold leading-tight text-gray-900 sm:text-sm lg:text-base">
                    {priceRange.min === priceRange.max 
                      ? formatCurrency(priceRange.min)
                      : `${formatCurrency(priceRange.min)} - ${formatCurrency(priceRange.max)}`
                    }
                  </p>
                </div>
              </div>
            </div>


            {!hasPackageBasedMaxClaims && (
              <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm transition-shadow sm:p-4 hover:shadow-md">
                <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
                  <div className="flex-shrink-0 self-start p-2 bg-blue-100 rounded-lg">
                    <Target className="w-4 h-4 text-blue-600 sm:w-5 sm:h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="mb-1 text-xs text-gray-500">Max Claims</p>
                    <p className="text-xs font-bold text-gray-900 sm:text-sm lg:text-base">
                      {policy.max_claims ?? "—"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm transition-shadow sm:p-4 hover:shadow-md">
              <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
                <div className="flex-shrink-0 self-start p-2 bg-purple-100 rounded-lg">
                  <Clock className="w-4 h-4 text-purple-600 sm:w-5 sm:h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="mb-1 text-xs text-gray-500">Waiting Period</p>
                  <p className="text-xs font-bold text-gray-900 sm:text-sm lg:text-base">
                    {policy.waiting_period} days
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6 sm:mb-8"
        >
          <div className="bg-white rounded-t-xl border-b border-gray-200">
            <nav
              className="flex overflow-x-auto px-2 scrollbar-hide sm:px-4 lg:px-6"
              aria-label="Tabs"
            >
              <div className="flex space-x-2 min-w-max sm:space-x-4 lg:space-x-8">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;

                  return (
                    <motion.button
                      key={tab.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center py-3 sm:py-4 px-2 sm:px-3 lg:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                        isActive
                          ? "text-blue-600 border-blue-500"
                          : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <Icon className="flex-shrink-0 mr-1 w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                      <span className="hidden sm:inline">{tab.label}</span>
                      <span className="sm:hidden">
                        {tab.label.split(" ")[0]}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </nav>
          </div>
        </motion.div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-t-none rounded-b-xl border border-gray-200 shadow-sm"
        >
          {activeTab === "overview" && (
            <div className="p-4 space-y-6 sm:p-6">
              {/* Policy Description */}
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <h3 className="flex gap-2 items-center mb-3 text-base font-semibold text-gray-900">
                  <Info className="w-4 h-4 text-gray-700" />
                  Policy Description
                </h3>
                <p className="text-sm leading-relaxed text-gray-700">
                  {policy.description}
                </p>
              </div>

              {/* Policy Details Grid */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Policy Information */}
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="mb-4 text-sm font-semibold text-gray-900">
                    Policy Information
                  </h4>
                  <div className="space-y-3">
                    {!hasPackageBasedMaxClaims && (
                      <div className="flex justify-between items-start py-2 border-b border-gray-100">
                        <div className="flex gap-2 items-center">
                          <Target className="flex-shrink-0 w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">Max Claims</span>
                        </div>
                        <span className="text-sm font-medium text-right text-gray-900">
                          {policy.max_claims ?? "—"}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between items-start py-2 border-b border-gray-100">
                      <div className="flex gap-2 items-center">
                        <Clock className="flex-shrink-0 w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Waiting Period</span>
                      </div>
                      <span className="text-sm font-medium text-right text-gray-900">
                        {policy.waiting_period} days
                      </span>
                    </div>

                    <div className="flex justify-between items-start py-2 border-b border-gray-100">
                      <div className="flex gap-2 items-center">
                        <Shield className="flex-shrink-0 w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Insurance Type</span>
                      </div>
                      <span className="text-sm font-medium text-right text-gray-900 capitalize">
                        {typeInfo.label}
                      </span>
                    </div>

                    {insuranceType === "tripsecure" && (
                      <div className="flex justify-between items-start py-2">
                        <div className="flex gap-2 items-center">
                          <MapPin className="flex-shrink-0 w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">Geography</span>
                        </div>
                        <span className="text-sm font-medium text-right text-gray-900 capitalize">
                          {policy.geography}
                        </span>
                      </div>
                    )}

                    {(isLifeInsurance || isGeneralInsurance) && packages.length > 0 && (
                      <div className="flex justify-between items-start py-2">
                        <div className="flex gap-2 items-center">
                          <Shield className="flex-shrink-0 w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">Total Packages</span>
                        </div>
                        <span className="text-sm font-medium text-right text-gray-900">
                          {packages.length}
                        </span>
                      </div>
                    )}
                    {isTravelInsurance && travelPackages.length > 0 && (
                      <div className="flex justify-between items-start py-2">
                        <div className="flex gap-2 items-center">
                          <Shield className="flex-shrink-0 w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">Total Packages</span>
                        </div>
                        <span className="text-sm font-medium text-right text-gray-900">
                          {travelPackages.length}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Eligibility Criteria - From Packages */}
                {(() => {
                  const relevantPackages = isTravelInsurance ? travelPackages : packages;
                  const hasEligibility = relevantPackages.length > 0 && relevantPackages[0].eligibility_criteria?.length > 0;
                  
                  if ((isLifeInsurance || isGeneralInsurance || (isTravelInsurance && policy.policy_type === "travel_insurance")) && hasEligibility) {
                    return (
                      <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <div className="flex gap-2 items-center mb-3">
                          <CheckCircle className="w-4 h-4 text-gray-700" />
                          <h4 className="text-sm font-semibold text-gray-900">Common Eligibility Criteria</h4>
                        </div>
                        <div className="space-y-2">
                          {relevantPackages[0].eligibility_criteria.map((criteria, index) => (
                            <div key={index} className="flex gap-2 items-start">
                              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{criteria}</span>
                            </div>
                          ))}
                        </div>
                        {relevantPackages.length > 1 && (
                          <p className="mt-3 text-xs italic text-gray-500">
                            Note: Each package may have specific eligibility criteria. View the Coverage tab for details.
                          </p>
                        )}
                      </div>
                    );
                  } else if (insuranceType === "tripsecure" && policy.eligibility_criteria?.length > 0) {
                    return (
                      <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <div className="flex gap-2 items-center mb-3">
                          <CheckCircle className="w-4 h-4 text-gray-700" />
                          <h4 className="text-sm font-semibold text-gray-900">Eligibility Criteria</h4>
                        </div>
                        <div className="space-y-2">
                          {policy.eligibility_criteria.map((criteria, index) => (
                            <div key={index} className="flex gap-2 items-start">
                              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{criteria}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Exclusions - From Packages */}
                {(() => {
                  const relevantPackages = isTravelInsurance ? travelPackages : packages;
                  const hasExclusions = relevantPackages.length > 0 && relevantPackages[0].exclusions?.length > 0;
                  
                  if ((isLifeInsurance || isGeneralInsurance || (isTravelInsurance && policy.policy_type === "travel_insurance")) && hasExclusions) {
                    return (
                      <div className="p-4 bg-white rounded-lg border border-gray-200 sm:col-span-2">
                        <div className="flex gap-2 items-center mb-3">
                          <XCircle className="w-4 h-4 text-gray-700" />
                          <h4 className="text-sm font-semibold text-gray-900">Common Exclusions</h4>
                        </div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {relevantPackages[0].exclusions.map((exclusion, index) => (
                            <div key={index} className="flex gap-2 items-start">
                              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{exclusion}</span>
                            </div>
                          ))}
                        </div>
                        {relevantPackages.length > 1 && (
                          <p className="mt-3 text-xs italic text-gray-500">
                            Note: Each package may have specific exclusions. View the Coverage tab for details.
                          </p>
                        )}
                      </div>
                    );
                  } else if (insuranceType === "tripsecure" && policy.exclusions?.length > 0) {
                    return (
                      <div className="p-4 bg-white rounded-lg border border-gray-200 sm:col-span-2">
                        <div className="flex gap-2 items-center mb-3">
                          <XCircle className="w-4 h-4 text-gray-700" />
                          <h4 className="text-sm font-semibold text-gray-900">Exclusions</h4>
                        </div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {policy.exclusions.map((exclusion, index) => (
                            <div key={index} className="flex gap-2 items-start">
                              <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{exclusion}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )}

          {activeTab === "coverage" && (
            <div className="p-4 space-y-6 sm:p-6">
              {/* Route-Based Coverage for Intercity */}
              {policy.policy_type === "intercity_travel" && (
                <div className="space-y-6">
                  {/* Regional Routes */}
                  {policy.coverage_durations.some(d => d.route_type === "regional") && (
                    <div>
                      <h3 className="flex items-center mb-4 text-base font-semibold text-gray-900 sm:text-lg">
                        <MapPin className="mr-2 w-4 h-4 text-blue-500 sm:w-5 sm:h-5" />
                        Regional Routes
                        <span className="px-2 py-1 ml-3 text-xs font-medium text-blue-600 bg-blue-50 rounded-full sm:text-sm sm:px-3">
                          {coverageDurations.filter(d => d.route_type === "regional").length} routes
                        </span>
                      </h3>
                      <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {coverageDurations
                          .filter(d => d.route_type === "regional")
                          .map((route, index) => (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.1 }}
                              whileHover={{ scale: 1.03, y: -4 }}
                              className="relative p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200 transition-all duration-300 hover:shadow-xl hover:border-blue-400"
                            >
                              <div className="absolute -top-2 -right-2">
                                <div className="px-2 py-1 text-xs font-bold text-white bg-blue-500 rounded-full shadow-lg">
                                  Regional
                                </div>
                              </div>
                              <div className="mb-3">
                                <p className="mb-1 text-xs text-gray-600">Route</p>
                                <p className="text-sm font-bold text-gray-900 break-words">
                                  Greater Accra → {route.route_destination}
                                </p>
                              </div>
                              <div className="flex justify-between items-center pt-3 border-t border-blue-200">
                                <div>
                                  <p className="text-xs text-gray-600">Duration</p>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {route.display_value} {route.display_unit}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-600">Price</p>
                                  <p className="text-lg font-extrabold text-blue-600">
                                    {formatCurrency(route.price)}
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* International Routes */}
                  {coverageDurations.some(d => d.route_type === "international") && (
                    <div>
                      <h3 className="flex items-center mb-4 text-base font-semibold text-gray-900 sm:text-lg">
                        <MapPin className="mr-2 w-4 h-4 text-indigo-500 sm:w-5 sm:h-5" />
                        International Routes
                        <span className="px-2 py-1 ml-3 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-full sm:text-sm sm:px-3">
                          {coverageDurations.filter(d => d.route_type === "international").length} routes
                        </span>
                      </h3>
                      <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {coverageDurations
                          .filter(d => d.route_type === "international")
                          .map((route, index) => (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.1 }}
                              whileHover={{ scale: 1.03, y: -4 }}
                              className="relative p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-200 transition-all duration-300 hover:shadow-xl hover:border-indigo-400"
                            >
                              <div className="absolute -top-2 -right-2">
                                <div className="px-2 py-1 text-xs font-bold text-white bg-indigo-500 rounded-full shadow-lg">
                                  International
                                </div>
                              </div>
                              <div className="mb-3">
                                <p className="mb-1 text-xs text-gray-600">Route</p>
                                <p className="text-sm font-bold text-gray-900 break-words">
                                  Greater Accra → {route.route_destination}
                                </p>
                              </div>
                              <div className="flex justify-between items-center pt-3 border-t border-indigo-200">
                                <div>
                                  <p className="text-xs text-gray-600">Duration</p>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {route.display_value} {route.display_unit}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-600">Price</p>
                                  <p className="text-lg font-extrabold text-indigo-600">
                                    {formatCurrency(route.price)}
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Motor Rider / Daily Commuter Coverage */}
              {(policy.policy_type === "okada_rider" || policy.policy_type === "daily_commuter") && coverageDurations.length > 0 && (
                <div>
                  <h3 className="flex items-center mb-4 text-base font-semibold text-gray-900 sm:text-lg">
                    <Zap className={`mr-2 w-4 h-4 sm:w-5 sm:h-5 ${policy.policy_type === "daily_commuter" ? "text-blue-500" : "text-orange-500"}`} />
                    {policy.policy_type === "daily_commuter" ? "Daily Commuter" : "Motor Rider"} Coverage Plans
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {coverageDurations.map((duration, index) => (
                      <motion.div
                        key={`${duration.display_value}-${duration.display_unit}-${index}`}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className={`p-6 rounded-2xl border-2 ${
                          policy.policy_type === "daily_commuter"
                            ? "bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200"
                            : "bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200"
                        }`}
                      >
                        <p className="mb-1 text-sm text-gray-600">Duration</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatFixedDurationLabel(duration)}
                        </p>
                        <p className="mt-4 mb-1 text-sm text-gray-600">Premium</p>
                        <p className={`text-3xl font-extrabold bg-clip-text text-transparent ${
                          policy.policy_type === "daily_commuter"
                            ? "bg-gradient-to-r from-blue-600 to-cyan-600"
                            : "bg-gradient-to-r from-orange-600 to-amber-600"
                        }`}>
                          {formatCurrency(getPrice(duration))}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Life Insurance Packages */}
              {isLifeInsurance && packages.length > 0 && (
                <div>
                  <h3 className="flex items-center mb-4 text-base font-semibold text-gray-900 sm:text-lg">
                    <Heart className="mr-2 w-4 h-4 text-blue-600 sm:w-5 sm:h-5" />
                    Life Insurance Packages
                    <span className="px-2 py-1 ml-3 text-xs font-medium text-gray-600 bg-gray-100 rounded-full sm:text-sm sm:px-3">
                      {packages.length} package{packages.length !== 1 ? "s" : ""}
                    </span>
                  </h3>
                  <div className="space-y-3">
                    {packages.map((pkg, index) => {
                      const isExpanded = expandedPackages[index];
                      return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow hover:shadow-md"
                      >
                        {/* Package Header - Always Visible */}
                        <button
                          onClick={() => togglePackage(index)}
                          className="flex justify-between items-center px-4 py-4 w-full text-left transition-colors hover:bg-gray-50 sm:px-6"
                        >
                          <div className="flex flex-col flex-1 gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex gap-2 items-center mb-1">
                                <h4 className="text-base font-semibold text-gray-900 sm:text-lg">
                                  {pkg.product_name || `Package ${index + 1}`}
                                </h4>
                                <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded">
                                  #{index + 1}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 line-clamp-1">
                                {pkg.description || "No description"}
                              </p>
                            </div>
                            <div className="flex gap-4 items-center sm:gap-6">
                              {hasPackageBasedMaxClaims && (pkg.max_claims != null || policy.max_claims != null) && (
                                <div>
                                  <p className="text-xs text-gray-500">Max Claims</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {pkg.max_claims ?? policy.max_claims ?? "—"}
                                  </p>
                                </div>
                              )}
                              <div>
                                <p className="text-xs text-gray-500">Duration</p>
                                <p className="text-sm font-medium text-gray-900">
                                  {pkg.duration || "N/A"}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-500">Premium</p>
                                <p className="text-lg font-bold text-gray-900">
                                  {pkg.is_quote_only ? (
                                    <span className="text-amber-600 font-semibold">Price on quote</span>
                                  ) : (
                                    formatCurrency(pkg.premium || 0)
                                  )}
                                </p>
                                {!pkg.is_quote_only && pkg.premium_duration && (
                                  <p className="text-xs text-gray-500 capitalize mt-0.5">
                                    per {pkg.premium_duration}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-4">
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </button>

                        {/* Collapsible Package Details */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden border-t border-gray-200"
                            >
                              <div className="grid grid-cols-1 gap-4 px-4 py-5 bg-gray-50 sm:grid-cols-2 sm:px-6">
                                {/* Benefits */}
                                {(pkg.insurance_coverage_list?.length || policy.insurance_coverage_list?.length) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                                    <div className="flex gap-2 items-center mb-3">
                                      <Shield className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Coverage Benefits</span>
                                    </div>
                                    <div className="space-y-2">
                                      {(pkg.insurance_coverage_list?.length ? pkg.insurance_coverage_list : policy.insurance_coverage_list).map((b, i) => (
                                        <div key={i} className="flex gap-2 items-start justify-between">
                                          <div className="flex gap-2 items-start flex-1">
                                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                                            <div className="text-sm text-gray-700 flex-1">
                                              <span className="font-medium">{b.title}</span>
                                              {b.description && <p className="text-xs text-gray-600 mt-0.5">{b.description}</p>}
                                            </div>
                                          </div>
                                          {b.limit && (
                                            <span className="text-sm font-semibold text-gray-900 whitespace-nowrap ml-2">
                                              {formatCurrency(Number(b.limit) || 0)}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Exclusions */}
                                {(pkg.exclusions?.length || policy.exclusions?.length) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                                    <div className="flex gap-2 items-center mb-3">
                                      <XCircle className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Exclusions</span>
                                    </div>
                                    <div className="space-y-2">
                                      {(pkg.exclusions?.length ? pkg.exclusions : policy.exclusions).map((ex, i) => (
                                        <div key={i} className="flex gap-2 items-start">
                                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                                          <div className="text-sm text-gray-700">{ex}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Eligibility */}
                                {(pkg.eligibility_criteria?.length || policy.eligibility_criteria?.length) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                                    <div className="flex gap-2 items-center mb-3">
                                      <CheckCircle className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Eligibility Criteria</span>
                                    </div>
                                    <div className="space-y-2">
                                      {(pkg.eligibility_criteria?.length ? pkg.eligibility_criteria : policy.eligibility_criteria).map((cr, i) => (
                                        <div key={i} className="flex gap-2 items-start">
                                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                                          <div className="text-sm text-gray-700">{cr}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Claim Process */}
                                {(pkg.claim_process_steps?.length || policy.claim_process_steps?.length) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                                    <div className="flex gap-2 items-center mb-3">
                                      <FileText className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Claim Process</span>
                                    </div>
                                    <div className="space-y-3">
                                      {(pkg.claim_process_steps?.length ? pkg.claim_process_steps : policy.claim_process_steps).map((step, i) => (
                                        <div key={i} className="flex gap-2 items-start">
                                          <div className="flex flex-shrink-0 justify-center items-center w-6 h-6 text-xs font-bold text-gray-700 bg-gray-200 rounded-full">
                                            {step.step_number || i + 1}
                                          </div>
                                          <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">{step.title}</p>
                                            {step.timeframe && <p className="text-xs text-gray-500 mt-0.5">Timeframe: {step.timeframe}</p>}
                                            {step.description && <p className="mt-1 text-xs text-gray-600">{step.description}</p>}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Terms & Conditions */}
                                {(pkg.terms || policy.terms) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200 sm:col-span-2">
                                    <div className="flex gap-2 items-center mb-3">
                                      <FileText className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Terms & Conditions</span>
                                    </div>
                                    <div className="text-sm text-gray-700">
                                      {String(pkg.terms || policy.terms).startsWith("http") ? (
                                        <a
                                          href={pkg.terms || policy.terms}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-blue-600 underline break-all hover:text-blue-700"
                                        >
                                          View Terms & Conditions Document
                                        </a>
                                      ) : (
                                        <div className="overflow-y-auto pr-2 max-h-48 whitespace-pre-wrap break-words">
                                          {pkg.terms || policy.terms}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* General Insurance Packages */}
              {isGeneralInsurance && packages.length > 0 && (
                <div>
                  <h3 className="flex items-center mb-4 text-base font-semibold text-gray-900 sm:text-lg">
                    <Home className="mr-2 w-4 h-4 text-purple-600 sm:w-5 sm:h-5" />
                    Insurance Packages
                    <span className="px-2 py-1 ml-3 text-xs font-medium text-gray-600 bg-gray-100 rounded-full sm:text-sm sm:px-3">
                      {packages.length} package{packages.length !== 1 ? "s" : ""}
                    </span>
                  </h3>
                  <div className="space-y-3">
                    {packages.map((pkg, index) => {
                      const isExpanded = expandedPackages[`general-${index}`];
                      return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow hover:shadow-md"
                      >
                        {/* Package Header - Always Visible */}
                        <button
                          onClick={() => togglePackage(`general-${index}`)}
                          className="flex justify-between items-center px-4 py-4 w-full text-left transition-colors hover:bg-gray-50 sm:px-6"
                        >
                          <div className="flex flex-col flex-1 gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex gap-2 items-center mb-1">
                                <h4 className="text-base font-semibold text-gray-900 sm:text-lg">
                                  {pkg.product_name || `Package ${index + 1}`}
                                </h4>
                                <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded">
                                  #{index + 1}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 line-clamp-1">
                                {pkg.description || "No description"}
                              </p>
                            </div>
                            <div className="flex gap-4 items-center sm:gap-6">
                              {hasPackageBasedMaxClaims && (pkg.max_claims != null || policy.max_claims != null) && (
                                <div>
                                  <p className="text-xs text-gray-500">Max Claims</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {pkg.max_claims ?? policy.max_claims ?? "—"}
                                  </p>
                                </div>
                              )}
                              <div>
                                <p className="text-xs text-gray-500">Duration</p>
                                <p className="text-sm font-medium text-gray-900">
                                  {pkg.duration || "N/A"}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-500">Premium</p>
                                <p className="text-lg font-bold text-gray-900">
                                  {pkg.is_quote_only ? (
                                    <span className="text-amber-600 font-semibold">Price on quote</span>
                                  ) : (
                                    formatCurrency(pkg.premium || 0)
                                  )}
                                </p>
                                {!pkg.is_quote_only && pkg.premium_duration && (
                                  <p className="text-xs text-gray-500 capitalize mt-0.5">
                                    per {pkg.premium_duration}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-4">
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </button>

                        {/* Collapsible Package Details */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden border-t border-gray-200"
                            >
                              <div className="grid grid-cols-1 gap-4 px-4 py-5 bg-gray-50 sm:grid-cols-2 sm:px-6">
                                {/* Benefits */}
                                {(pkg.insurance_coverage_list?.length || policy.insurance_coverage_list?.length) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                                    <div className="flex gap-2 items-center mb-3">
                                      <Shield className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Coverage Benefits</span>
                                    </div>
                                    <div className="space-y-2">
                                      {(pkg.insurance_coverage_list?.length ? pkg.insurance_coverage_list : policy.insurance_coverage_list).map((b, i) => (
                                        <div key={i} className="flex gap-2 items-start justify-between">
                                          <div className="flex gap-2 items-start flex-1">
                                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                                            <div className="text-sm text-gray-700 flex-1">
                                              <span className="font-medium">{b.title}</span>
                                              {b.description && <p className="text-xs text-gray-600 mt-0.5">{b.description}</p>}
                                            </div>
                                          </div>
                                          {b.limit && (
                                            <span className="text-sm font-semibold text-gray-900 whitespace-nowrap ml-2">
                                              {formatCurrency(Number(b.limit) || 0)}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Exclusions */}
                                {(pkg.exclusions?.length || policy.exclusions?.length) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                                    <div className="flex gap-2 items-center mb-3">
                                      <XCircle className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Exclusions</span>
                                    </div>
                                    <div className="space-y-2">
                                      {(pkg.exclusions?.length ? pkg.exclusions : policy.exclusions).map((ex, i) => (
                                        <div key={i} className="flex gap-2 items-start">
                                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                                          <div className="text-sm text-gray-700">{ex}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Eligibility */}
                                {(pkg.eligibility_criteria?.length || policy.eligibility_criteria?.length) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                                    <div className="flex gap-2 items-center mb-3">
                                      <CheckCircle className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Eligibility Criteria</span>
                                    </div>
                                    <div className="space-y-2">
                                      {(pkg.eligibility_criteria?.length ? pkg.eligibility_criteria : policy.eligibility_criteria).map((cr, i) => (
                                        <div key={i} className="flex gap-2 items-start">
                                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                                          <div className="text-sm text-gray-700">{cr}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Claim Process */}
                                {(pkg.claim_process_steps?.length || policy.claim_process_steps?.length) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                                    <div className="flex gap-2 items-center mb-3">
                                      <FileText className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Claim Process</span>
                                    </div>
                                    <div className="space-y-3">
                                      {(pkg.claim_process_steps?.length ? pkg.claim_process_steps : policy.claim_process_steps).map((step, i) => (
                                        <div key={i} className="flex gap-2 items-start">
                                          <div className="flex flex-shrink-0 justify-center items-center w-6 h-6 text-xs font-bold text-gray-700 bg-gray-200 rounded-full">
                                            {step.step_number || i + 1}
                                          </div>
                                          <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">{step.title}</p>
                                            {step.timeframe && <p className="text-xs text-gray-500 mt-0.5">Timeframe: {step.timeframe}</p>}
                                            {step.description && <p className="mt-1 text-xs text-gray-600">{step.description}</p>}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Terms & Conditions */}
                                {(pkg.terms || policy.terms) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200 sm:col-span-2">
                                    <div className="flex gap-2 items-center mb-3">
                                      <FileText className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Terms & Conditions</span>
                                    </div>
                                    <div className="text-sm text-gray-700">
                                      {String(pkg.terms || policy.terms).startsWith("http") ? (
                                        <a
                                          href={pkg.terms || policy.terms}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-blue-600 underline break-all hover:text-blue-700"
                                        >
                                          View Terms & Conditions Document
                                        </a>
                                      ) : (
                                        <div className="overflow-y-auto pr-2 max-h-48 whitespace-pre-wrap break-words">
                                          {pkg.terms || policy.terms}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Travel Insurance Packages */}
              {isTravelInsurance && policy.policy_type === "travel_insurance" && travelPackages.length > 0 && (
                <div>
                  <h3 className="flex items-center mb-4 text-base font-semibold text-gray-900 sm:text-lg">
                    <Shield className="mr-2 w-4 h-4 text-indigo-600 sm:w-5 sm:h-5" />
                    Travel Insurance Packages
                    <span className="px-2 py-1 ml-3 text-xs font-medium text-gray-600 bg-gray-100 rounded-full sm:text-sm sm:px-3">
                      {travelPackages.length} package{travelPackages.length !== 1 ? "s" : ""}
                    </span>
                  </h3>
                  <div className="space-y-3">
                    {travelPackages.map((pkg, index) => {
                      const isExpanded = expandedPackages[`travel-${index}`];
                      return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow hover:shadow-md"
                      >
                        {/* Package Header - Always Visible */}
                        <button
                          onClick={() => togglePackage(`travel-${index}`)}
                          className="flex justify-between items-center px-4 py-4 w-full text-left transition-colors hover:bg-gray-50 sm:px-6"
                        >
                          <div className="flex flex-col flex-1 gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex gap-2 items-center mb-1">
                                <h4 className="text-base font-semibold text-gray-900 sm:text-lg">
                                  {pkg.product_name || `Package ${index + 1}`}
                                </h4>
                                <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded">
                                  #{index + 1}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 line-clamp-1">
                                {pkg.description || "No description"}
                              </p>
                            </div>
                            <div className="flex gap-4 items-center sm:gap-6">
                              {hasPackageBasedMaxClaims && (pkg.max_claims != null || policy.max_claims != null) && (
                                <div>
                                  <p className="text-xs text-gray-500">Max Claims</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {pkg.max_claims ?? policy.max_claims ?? "—"}
                                  </p>
                                </div>
                              )}
                              <div>
                                <p className="text-xs text-gray-500">Duration</p>
                                <p className="text-sm font-medium text-gray-900">
                                  {pkg.duration || "N/A"}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-500">Premium</p>
                                <p className="text-lg font-bold text-gray-900">
                                  {pkg.is_quote_only ? (
                                    <span className="text-amber-600 font-semibold">Price on quote</span>
                                  ) : (
                                    formatCurrency(pkg.premium || 0)
                                  )}
                                </p>
                                {!pkg.is_quote_only && pkg.premium_duration && (
                                  <p className="text-xs text-gray-500 capitalize mt-0.5">
                                    per {pkg.premium_duration}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-4">
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </button>

                        {/* Collapsible Package Details */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden border-t border-gray-200"
                            >
                              <div className="grid grid-cols-1 gap-4 px-4 py-5 bg-gray-50 sm:grid-cols-2 sm:px-6">
                                {/* Benefits */}
                                {(pkg.insurance_coverage_list?.length || policy.insurance_coverage_list?.length) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                                    <div className="flex gap-2 items-center mb-3">
                                      <Shield className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Coverage Benefits</span>
                                    </div>
                                    <div className="space-y-2">
                                      {(pkg.insurance_coverage_list?.length ? pkg.insurance_coverage_list : policy.insurance_coverage_list).map((b, i) => (
                                        <div key={i} className="flex gap-2 items-start justify-between">
                                          <div className="flex gap-2 items-start flex-1">
                                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                                            <div className="text-sm text-gray-700 flex-1">
                                              <span className="font-medium">{b.title}</span>
                                              {b.description && <p className="text-xs text-gray-600 mt-0.5">{b.description}</p>}
                                            </div>
                                          </div>
                                          {b.limit && (
                                            <span className="text-sm font-semibold text-gray-900 whitespace-nowrap ml-2">
                                              {formatCurrency(Number(b.limit) || 0)}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Exclusions */}
                                {(pkg.exclusions?.length || policy.exclusions?.length) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                                    <div className="flex gap-2 items-center mb-3">
                                      <XCircle className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Exclusions</span>
                                    </div>
                                    <div className="space-y-2">
                                      {(pkg.exclusions?.length ? pkg.exclusions : policy.exclusions).map((ex, i) => (
                                        <div key={i} className="flex gap-2 items-start">
                                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                                          <div className="text-sm text-gray-700">{ex}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Eligibility */}
                                {(pkg.eligibility_criteria?.length || policy.eligibility_criteria?.length) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                                    <div className="flex gap-2 items-center mb-3">
                                      <CheckCircle className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Eligibility Criteria</span>
                                    </div>
                                    <div className="space-y-2">
                                      {(pkg.eligibility_criteria?.length ? pkg.eligibility_criteria : policy.eligibility_criteria).map((cr, i) => (
                                        <div key={i} className="flex gap-2 items-start">
                                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                                          <div className="text-sm text-gray-700">{cr}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Claim Process */}
                                {(pkg.claim_process_steps?.length || policy.claim_process_steps?.length) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                                    <div className="flex gap-2 items-center mb-3">
                                      <FileText className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Claim Process</span>
                                    </div>
                                    <div className="space-y-3">
                                      {(pkg.claim_process_steps?.length ? pkg.claim_process_steps : policy.claim_process_steps).map((step, i) => (
                                        <div key={i} className="flex gap-2 items-start">
                                          <div className="flex flex-shrink-0 justify-center items-center w-6 h-6 text-xs font-bold text-gray-700 bg-gray-200 rounded-full">
                                            {step.step_number || i + 1}
                                          </div>
                                          <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">{step.title}</p>
                                            {step.timeframe && <p className="text-xs text-gray-500 mt-0.5">Timeframe: {step.timeframe}</p>}
                                            {step.description && <p className="mt-1 text-xs text-gray-600">{step.description}</p>}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Terms & Conditions */}
                                {(pkg.terms || policy.terms) && (
                                  <div className="p-4 bg-white rounded-lg border border-gray-200 sm:col-span-2">
                                    <div className="flex gap-2 items-center mb-3">
                                      <FileText className="w-4 h-4 text-gray-700" />
                                      <span className="text-sm font-semibold text-gray-900">Terms & Conditions</span>
                                    </div>
                                    <div className="text-sm text-gray-700">
                                      {String(pkg.terms || policy.terms).startsWith("http") ? (
                                        <a
                                          href={pkg.terms || policy.terms}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-blue-600 underline break-all hover:text-blue-700"
                                        >
                                          View Terms & Conditions Document
                                        </a>
                                      ) : (
                                        <div className="overflow-y-auto pr-2 max-h-48 whitespace-pre-wrap break-words">
                                          {pkg.terms || policy.terms}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Insurance Coverage List */}
              {policy.insurance_coverage_list && policy.insurance_coverage_list.length > 0 && (
                <div>
                  <h3 className="flex gap-2 items-center mb-4 text-base font-semibold text-gray-900 sm:text-lg">
                    <Shield className="w-4 h-4 text-gray-700 sm:w-5 sm:h-5" />
                    Insurance Coverage Details
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {policy.insurance_coverage_list.map((coverage, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-4 bg-white rounded-lg border border-gray-200"
                      >
                        <h4 className="mb-2 text-sm font-semibold text-gray-900">
                          {coverage.title}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {coverage.description}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "claims" && (
            <div className="p-4 space-y-6 sm:p-6">
              {/* Package-based Claims Process for Life, General, and Travel Insurance */}
              {(isLifeInsurance || isGeneralInsurance || (isTravelInsurance && policy.policy_type === "travel_insurance")) && (
                <>
                  {(() => {
                    const relevantPackages = isTravelInsurance ? travelPackages : packages;
                    
                    if (relevantPackages.length === 0) {
                      return (
                        <div className="p-8 text-center">
                          <FileText className="mx-auto mb-4 w-12 h-12 text-gray-400" />
                          <p className="text-gray-600">No packages available</p>
                        </div>
                      );
                    }

                    return relevantPackages.map((pkg, pkgIndex) => {
                      const hasClaimSteps = pkg.claim_process_steps && pkg.claim_process_steps.length > 0;
                      
                      if (!hasClaimSteps) return null;

                      return (
                        <motion.div
                          key={pkgIndex}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: pkgIndex * 0.1 }}
                          className="p-4 bg-white rounded-lg border border-gray-200 sm:p-6"
                        >
                          {/* Package Header */}
                          <div className="pb-4 mb-4 border-b border-gray-200">
                            <div className="flex flex-wrap gap-3 justify-between items-center">
                              <div>
                                <h3 className="flex gap-2 items-center text-base font-semibold text-gray-900 sm:text-lg">
                                  <FileText className="w-5 h-5 text-gray-700" />
                                  {pkg.product_name || `Package ${pkgIndex + 1}`}
                                </h3>
                                {pkg.description && (
                                  <p className="mt-1 text-sm text-gray-600 line-clamp-2">{pkg.description}</p>
                                )}
                              </div>
                              <div className="flex gap-4 items-center">
                                <div className="text-right">
                                  <p className="text-xs text-gray-500">Duration</p>
                                  <p className="text-sm font-medium text-gray-900">{pkg.duration}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-500">Premium</p>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {pkg.is_quote_only ? (
                                      <span className="text-amber-600">Price on quote</span>
                                    ) : (
                                      formatCurrency(pkg.premium || 0)
                                    )}
                                  </p>
                                  {!pkg.is_quote_only && pkg.premium_duration && (
                                    <p className="text-xs text-gray-500 capitalize">
                                      per {pkg.premium_duration}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Claim Process Steps */}
                          <div className="space-y-4">
                            {pkg.claim_process_steps.map((step, stepIndex) => (
                              <div key={stepIndex} className="relative">
                                {/* Connection line */}
                                {stepIndex < pkg.claim_process_steps.length - 1 && (
                                  <div className="absolute left-5 top-12 w-0.5 h-full bg-gray-200" />
                                )}

                                <div className="flex gap-4 items-start">
                                  <div className="flex-shrink-0">
                                    <div className="flex justify-center items-center w-10 h-10 bg-gray-200 rounded-full">
                                      <span className="text-sm font-bold text-gray-700">
                                        {step.step_number || stepIndex + 1}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  <div className="flex-1 pb-6">
                                    <h4 className="mb-2 text-base font-semibold text-gray-900">
                                      {step.title}
                                    </h4>
                                    <p className="mb-3 text-sm text-gray-600">
                                      {step.description}
                                    </p>
                                    
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                      {step.timeframe && (
                                        <div className="flex gap-2 items-start">
                                          <Clock className="flex-shrink-0 w-4 h-4 text-gray-400 mt-0.5" />
                                          <div>
                                            <p className="text-xs font-medium text-gray-700">Timeframe</p>
                                            <p className="text-sm text-gray-600">{step.timeframe}</p>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {step.requirements && (
                                        <div className="flex gap-2 items-start">
                                          <FileText className="flex-shrink-0 w-4 h-4 text-gray-400 mt-0.5" />
                                          <div>
                                            <p className="text-xs font-medium text-gray-700">Requirements</p>
                                            <p className="text-sm text-gray-600">{step.requirements}</p>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      );
                    });
                  })()}
                </>
              )}

              {/* Policy-level Claims Process for TripSecure and other types */}
              {(!isLifeInsurance && !isGeneralInsurance && !(isTravelInsurance && policy.policy_type === "travel_insurance")) && policy.claim_process_steps && policy.claim_process_steps.length > 0 && (
                <div className="p-4 bg-white rounded-lg border border-gray-200 sm:p-6">
                  <h3 className="flex gap-2 items-center mb-6 text-base font-semibold text-gray-900 sm:text-lg">
                    <FileText className="w-5 h-5 text-gray-700" />
                    Claims Process Steps
                  </h3>
                  
                  <div className="space-y-4">
                    {policy.claim_process_steps.map((step, index) => (
                      <div key={index} className="relative">
                        {/* Connection line */}
                        {index < policy.claim_process_steps.length - 1 && (
                          <div className="absolute left-5 top-12 w-0.5 h-full bg-gray-200" />
                        )}

                        <div className="flex gap-4 items-start">
                          <div className="flex-shrink-0">
                            <div className="flex justify-center items-center w-10 h-10 bg-gray-200 rounded-full">
                              <span className="text-sm font-bold text-gray-700">
                                {step.step_number || index + 1}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex-1 pb-6">
                            <h4 className="mb-2 text-base font-semibold text-gray-900">
                              {step.title}
                            </h4>
                            <p className="mb-3 text-sm text-gray-600">
                              {step.description}
                            </p>
                            
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              {step.timeframe && (
                                <div className="flex gap-2 items-start">
                                  <Clock className="flex-shrink-0 w-4 h-4 text-gray-400 mt-0.5" />
                                  <div>
                                    <p className="text-xs font-medium text-gray-700">Timeframe</p>
                                    <p className="text-sm text-gray-600">{step.timeframe}</p>
                                  </div>
                                </div>
                              )}
                              
                              {step.requirements && (
                                <div className="flex gap-2 items-start">
                                  <FileText className="flex-shrink-0 w-4 h-4 text-gray-400 mt-0.5" />
                                  <div>
                                    <p className="text-xs font-medium text-gray-700">Requirements</p>
                                    <p className="text-sm text-gray-600">{step.requirements}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

        </motion.div>
      </div>

      {/* Custom CSS for scrollbar hiding */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
